import { Info } from "lucide-react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useLang } from "@/context/LanguageContext";

/**
 * Small info button shown next to the LR values that opens a popover
 * with a Jaeschke-style interpretation table.
 */
export default function LRInfo() {
  const { t } = useLang();
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          aria-label={t("lr.title")}
          data-testid="lr-info-btn"
          className="inline-flex items-center justify-center size-4 rounded-full text-muted-foreground hover:text-foreground hover:bg-muted transition-colors ml-1 align-text-bottom"
        >
          <Info className="size-3" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-[320px] bg-card text-foreground p-4 text-xs"
        data-testid="lr-info-popover"
      >
        <div className="text-[11px] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
          <span className="title-rule" />
          {t("lr.title")}
        </div>
        <p className="text-xs text-muted-foreground leading-relaxed mb-3">
          {t("lr.lead")}
        </p>

        <table className="w-full text-[11px] mb-3 border-collapse">
          <thead>
            <tr className="text-muted-foreground font-mono text-[10px] uppercase tracking-wider">
              <th className="text-left pb-1.5 font-normal">LR+</th>
              <th className="text-left pb-1.5 font-normal">{t("lr.ruleIn")}</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            <tr><td className="py-0.5 text-primary">&gt; 10</td><td className="py-0.5">{t("lr.strong")}</td></tr>
            <tr><td className="py-0.5 text-accent">5 – 10</td><td className="py-0.5">{t("lr.moderate")}</td></tr>
            <tr><td className="py-0.5 text-muted-foreground">2 – 5</td><td className="py-0.5">{t("lr.weak")}</td></tr>
            <tr><td className="py-0.5 text-muted-foreground">1 – 2</td><td className="py-0.5">{t("lr.minimal")}</td></tr>
          </tbody>
        </table>

        <table className="w-full text-[11px] border-collapse">
          <thead>
            <tr className="text-muted-foreground font-mono text-[10px] uppercase tracking-wider">
              <th className="text-left pb-1.5 font-normal">LR–</th>
              <th className="text-left pb-1.5 font-normal">{t("lr.ruleOut")}</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            <tr><td className="py-0.5 text-primary">&lt; 0.1</td><td className="py-0.5">{t("lr.strong")}</td></tr>
            <tr><td className="py-0.5 text-accent">0.1 – 0.2</td><td className="py-0.5">{t("lr.moderate")}</td></tr>
            <tr><td className="py-0.5 text-muted-foreground">0.2 – 0.5</td><td className="py-0.5">{t("lr.weak")}</td></tr>
            <tr><td className="py-0.5 text-muted-foreground">0.5 – 1</td><td className="py-0.5">{t("lr.minimal")}</td></tr>
          </tbody>
        </table>

        <p className="text-[10px] text-muted-foreground/80 mt-3 italic">
          {t("lr.cite")}
        </p>
      </PopoverContent>
    </Popover>
  );
}
